// sede-modificacion.component.ts
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { GuiMsjService } from 'src/app/services/shared/gui-msj.service';
import { SignoSede } from 'src/app/interfaces/interface-bakend/entidades/signo-sede.interface';
import { SignoSedeService } from 'src/app/services/entidades/signo-sedes.service';
import { AppOfertas } from 'src/app/app.config/app.config-ofertas';

// Validador: “ambos o ninguno”
// - 0, null, undefined o '' se consideran “sin estado”
// - si el email está deshabilitado, se ignora para la validación
function bothOrNoneValidator(estadoKey: string, emailKey: string): ValidatorFn {
    return (group: AbstractControl) => {
        const estadoCtrl = group.get(estadoKey);
        const emailCtrl = group.get(emailKey);

        const estado = estadoCtrl?.value;
        const email = (emailCtrl?.value ?? '').toString().trim();

        const sinEstado = (estado === 0 || estado === null || estado === undefined || estado === '');
        const emailHabilitado = !!emailCtrl && !emailCtrl.disabled;
        const hayEmail = emailHabilitado && email.length > 0;

        // Válido si: (sin estado y sin email) o (con estado y con email)
        return ((sinEstado && !hayEmail) || (!sinEstado && hayEmail)) ? null : { bothOrNone: true };
    };
}

@Component({
    selector: 'app-sede-modificacion',
    templateUrl: './sede-modificacion.component.html',
    styleUrls: ['./sede-modificacion.component.scss'],
})
export class SedeModificacionComponent implements OnInit {

    form: FormGroup;
    signoSede: SignoSede;
    flagLoaded: boolean = false;

    // Estados válidos: 2..6
    // estadosNotificacion = AppOfertas.selectEstados.filter(e => e.number >= 2 && e.number <= 6);
    estadosNotificacion = AppOfertas.selectEstados.filter(e => e.number === 3);


    constructor(
        private router: Router,
        private signoSedeService: SignoSedeService,
        private guiMsjService: GuiMsjService
    ) { }

    ngOnInit(): void {
        this.getEstadoSede();
        this.createForm();
    }

    get codigoNoValido() { return this.form.get('codigo')?.invalid; }
    get nombreNoValido() { return this.form.get('nombre')?.invalid; }
    get emailNoValido() { return this.form.get('email')?.invalid; }
    get telefonoNoValido() { return this.form.get('telefono')?.invalid; }
    get whatsappNoValido() { return this.form.get('whatsapp')?.invalid; }
    get detalleNoValido() { return this.form.get('detalle')?.invalid; }
    get notificacionEmailNoValido() { return this.form.get('notificacionEmail')?.invalid; }
    get notificacionEstadoNoValido() { return this.form.get('notificacionEstado')?.invalid; }

    createForm() {
        // normalizamos el estado inicial (si viene 0 desde DB, usamos 0 igualmente para “no seleccionar estado”)
        const initialEstado = this.signoSede?.notificacionSetup?.length
            ? this.signoSede.notificacionSetup[0].codigoEstado
            : 0;

        this.form = new FormGroup({
            codigo: new FormControl(this.signoSede?.codigoGecro, [Validators.required]),
            nombre: new FormControl(this.signoSede?.nombre, [
                Validators.required, Validators.minLength(3), Validators.maxLength(50),
            ]),
            email: new FormControl(this.signoSede?.email, [
                Validators.pattern('[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,}$'),
                Validators.email
            ]),
            telefono: new FormControl(this.signoSede?.telefono, [
                Validators.minLength(3), Validators.maxLength(50), Validators.pattern('[0-9+-]+$')
            ]),
            whatsapp: new FormControl(this.signoSede?.whatsapp, [
                Validators.minLength(3), Validators.maxLength(50), Validators.pattern('[0-9+-]+$')
            ]),
            consultorioInterno: new FormControl(this.signoSede?.consultorioInterno, [Validators.required]),
            detalle: new FormControl(this.signoSede?.detalle, [
                Validators.minLength(3), Validators.maxLength(256),
            ]),

            // Estado/Email sin required; el form group valida “ambos o ninguno”
            notificacionEstado: new FormControl(initialEstado),
            notificacionEmail: new FormControl(
                this.signoSede?.notificacionSetup?.length ? this.signoSede.notificacionSetup[0].email : '',
                [
                    Validators.pattern('[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,}$'),
                    Validators.email
                ]
            ),
            adjuntar: new FormControl(
                this.signoSede?.notificacionSetup?.length ? this.signoSede.notificacionSetup[0].adjuntar : false
            ),
        }, { validators: [bothOrNoneValidator('notificacionEstado', 'notificacionEmail')] });

        // Vincula estado → email (deshabilita y limpia email cuando estado = 0/null/'')
        const estadoCtrl = this.form.get('notificacionEstado');
        const emailCtrl = this.form.get('notificacionEmail');

        const toggleEmail = (estadoVal: any) => {
            const sinEstado = (estadoVal === 0 || estadoVal === null || estadoVal === undefined || estadoVal === '');
            if (sinEstado) {
                emailCtrl?.setValue('', { emitEvent: false });
                emailCtrl?.disable({ emitEvent: false });
            } else {
                emailCtrl?.enable({ emitEvent: false });
            }
            // Revalida el form con el nuevo estado del email
            this.form.updateValueAndValidity({ emitEvent: false });
        };

        toggleEmail(estadoCtrl?.value);
        estadoCtrl?.valueChanges.subscribe(val => toggleEmail(val));
    }

    async getSede() {
        return (this.signoSede = this.signoSedeService.getSedeSeleccionada());
    }

    async getEstadoSede() {
        if (this.getSede() === undefined) {
            await this.router.navigateByUrl('/sedes-listado');
        } else {
            await this.getSede();
            this.flagLoaded = true;
        }
    }

    async updateSede() {
        if (!this.form.valid) {
            this.form.markAllAsTouched();
            return;
        }

        await this.guiMsjService.msjFormSubmit('Guardando');
        await this.signoSedeService.updateSede(this.signoSede._id, await this.setDataForma());
        await this.guiMsjService.msjFormSubmit('DatosGuardados');
        await this.router.navigateByUrl('/sedes-listado');
    }

    async setDataForma(): Promise<SignoSede> {
        const estado = this.form.value.notificacionEstado;
        const email = (this.form.value.notificacionEmail || '').trim();
        const adjuntar = !!this.form.value.adjuntar;

        const sinEstado = (estado === 0 || estado === null || estado === undefined || estado === '');
        const hayEmail = email.length > 0;

        const notificacionSetup = (!sinEstado && hayEmail)
            ? [{ codigoEstado: estado, email, adjuntar }]
            : [];

        const signoSede: SignoSede = {
            codigoGecro: this.form.value.codigo || '',
            _id: this.signoSede._id,
            nombre: this.form.value.nombre || '',
            email: this.form.value.email || '',
            telefono: this.form.value.telefono || '',
            whatsapp: this.form.value.whatsapp || '',
            consultorioInterno: this.form.value.consultorioInterno || false,
            detalle: this.form.value.detalle || '',
            notificacionSetup
        };

        return signoSede;
    }
}
